import { Card, CardContent, CardHeader, Input, ScrollArea } from '@ibsys/kit';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  useExpertReport,
  useUpdateExpertReport,
  findParentBlockId,
  findPointsRuleOwner,
  findPointsRuleOwnerOfChildren,
  flattenBlocks,
  mapRules,
  toPredicateInput,
  type BlockResponse,
  type RuleInBlockUpdate,
  type RuleResponse,
} from '@/entities/expert-report';
import { useHasPermission } from '@/entities/profile';
import { PointsRuleInput, type PointsRuleValue } from '@/features/expert-report/PointsRuleInput/PointsRuleInput';
import { usePointsRuleChangeWarning } from '@/features/expert-report/usePointsRuleChangeWarning';
import { useResultsRecalculationNotice } from '@/features/expert-report/useResultsRecalculationNotice';
import { toastWarning } from '@/shared/lib';
import { ConfirmationButton, SaveButton } from '@/shared/ui';
import { BlockPointsField } from './BlockPointsField';
import { RuleList } from './RuleList';
import { type BlockCardFormValues, type EditableRule } from './types';

type BlockCardProps = {
  reportId: number;
  blockId: number;
  onBlockDeleted: () => void;
  className?: string;
};

/** Рекурсивно ищет блок в дереве по идентификатору */
const findBlock = (blocks: BlockResponse[], blockId: number): BlockResponse | undefined => {
  for (const block of blocks) {
    if (block.id === blockId) return block;
    const found = findBlock(block.children, blockId);
    if (found) return found;
  }
  return undefined;
};

/** Собирает список сохраняемых правил из отредактированных в форме */
const buildRuleUpdates = (editedRules: EditableRule[]): RuleInBlockUpdate[] =>
  editedRules.map((r, index) => ({
    id: r.id,
    name: r.name,
    text: r.text,
    orderPosition: index,
    isDeleted: false,
    predicate: r.predicate?.dsl ? { dsl: r.predicate.dsl, dslHuman: r.predicate.dslHuman } : undefined,
    knowledgeSetIds: r.knowledgeSets.map((ks) => ks.id),
  }));

/** Собирает список удалённых правил: были у блока, но убраны из формы */
const buildDeletedRules = (editedRules: EditableRule[], originalRules: RuleResponse[]): RuleInBlockUpdate[] => {
  const editedRuleIds = new Set(editedRules.filter((r) => r.id != null).map((r) => r.id!));
  return originalRules
    .filter((or) => !editedRuleIds.has(or.id))
    .map((or) => ({
      ...mapRules([or])[0],
      isDeleted: true,
    }));
};

/** Правило расчёта блока в том виде, в каком его правит форма */
const mapBlockToFormPointsRule = (block: BlockResponse): PointsRuleValue => ({
  dsl: block.pointsRule?.dsl ?? '',
  dslHuman: block.pointsRule?.dslHuman ?? '',
});

const mapBlockToFormRules = (block: BlockResponse): EditableRule[] =>
  block.rules
    .filter((r) => !r.isDeleted)
    .map((r) => ({
      id: r.id,
      name: r.name,
      text: r.text,
      predicate: r.predicate ? { dsl: r.predicate.dsl ?? '', dslHuman: r.predicate.dslHuman ?? '' } : undefined,
      knowledgeSets: r.knowledgeSets ?? [],
    }));

export const BlockCard: FC<BlockCardProps> = ({ reportId, blockId, onBlockDeleted, className }) => {
  const { t } = useTranslation(['common', 'expertReport']);
  const { data: report, isLoading } = useExpertReport(reportId);
  const { mutateAsync: updateReport, isPending: isUpdating } = useUpdateExpertReport();
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const warnAboutPointsRule = usePointsRuleChangeWarning();
  const noticeResultsRecalculation = useResultsRecalculationNotice();
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Баллы в прошлом сохранении определить не удалось — даём повторить попытку без правок в карточке
  const [hasPointsFailure, setHasPointsFailure] = useState(false);

  const block = useMemo(() => {
    if (!report) return undefined;
    return findBlock(report.blocks, blockId);
  }, [report, blockId]);

  const form = useForm<BlockCardFormValues>({
    defaultValues: {
      name: block?.name ?? '',
      pointsRule: block ? mapBlockToFormPointsRule(block) : { dsl: '', dslHuman: '' },
      unit: block?.unit ?? '',
      rules: block ? mapBlockToFormRules(block) : [],
    },
  });

  const { control, reset, handleSubmit, formState } = form;

  useEffect(() => {
    if (block) {
      reset({
        name: block.name,
        pointsRule: mapBlockToFormPointsRule(block),
        unit: block.unit ?? '',
        rules: mapBlockToFormRules(block),
      });
    }
  }, [block, reset]);

  const isLeafBlock = useMemo(() => (block ? block.children.filter((c) => !c.isDeleted).length === 0 : false), [block]);

  const focusNameInput = useCallback(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, []);

  // При переходе к другому блоку фокус переносим на поле названия, а неудачу расчёта
  // прошлого блока забываем — она относится только к нему
  useEffect(() => {
    setHasPointsFailure(false);
    const timer = setTimeout(focusNameInput, 100);
    return () => clearTimeout(timer);
  }, [blockId, focusNameInput]);

  const onSubmit = async (data: BlockCardFormValues) => {
    if (!report) return;

    const originalRules = block?.rules.filter((r) => !r.isDeleted) ?? [];

    const updatedReport = await updateReport({
      id: reportId,
      body: {
        name: report.name,
        predicate: toPredicateInput(report.predicate),
        // Баллы определяются только сохраняемому блоку — остальные блоки заключения не трогаем
        pointsBlockId: blockId,
        blocks: flattenBlocks(report.blocks, null, (b) => {
          if (b.id !== blockId) return {};

          const updatedRules = buildRuleUpdates(data.rules);
          const deletedRules = buildDeletedRules(data.rules, originalRules);

          return {
            name: data.name.trim(),
            // Правило расчёта несёт только блок с вложенными блоками — листовому оно запрещено
            pointsRule: isLeafBlock ? undefined : data.pointsRule,
            // Размерность уходит вместе с остальными полями: заданную пользователем сервер
            // отличает от определённой по правилам сам, а пустое значение снимает размерность
            unit: data.unit.trim(),
            rules: [...updatedRules, ...deletedRules],
          };
        }),
      },
    });

    // Правило владельца ссылается на этот блок по названию и само за ним не меняется —
    // предупреждаем по тому, что стало с правилом после сохранения
    warnAboutPointsRule([findPointsRuleOwner(updatedReport, blockId)]);

    noticeResultsRecalculation(updatedReport);

    // Сохранение остаётся в силе, даже если баллы определить не удалось, — предупреждаем отдельно
    const pointsError = findBlock(updatedReport.blocks, blockId)?.pointsError;
    setHasPointsFailure(Boolean(pointsError));
    if (pointsError) {
      toastWarning(t('expertReport:pointsResolveFailed'), { description: pointsError });
    }
  };

  const handleDelete = async () => {
    if (!report) return;
    // Удаление меняет состав вложенных блоков у владельца правила расчёта; удалённый блок
    // владельца уже не укажет, поэтому запоминаем его до сохранения
    const parentBlockId = findParentBlockId(report, blockId);
    const updatedReport = await updateReport({
      id: reportId,
      body: {
        name: report.name,
        predicate: toPredicateInput(report.predicate),
        blocks: flattenBlocks(report.blocks, null, (block) => (block.id === blockId ? { isDeleted: true } : {})),
      },
    });
    warnAboutPointsRule([findPointsRuleOwnerOfChildren(updatedReport, parentBlockId)]);
    noticeResultsRecalculation(updatedReport);
    onBlockDeleted();
  };

  if (isLoading) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('common:loading')}</div>
        </CardHeader>
      </Card>
    );
  }

  if (!block) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('expertReport:selectItem')}</div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <FormProvider {...form}>
      <Card className={className}>
        <CardHeader className="space-y-4">
          <Input
            ref={nameInputRef}
            control={control}
            name="name"
            label={t('expertReport:blockName')}
            disabled={!allowAdmin}
            rules={{
              required: { value: true, message: t('common:required') },
              // Имя из одних пробелов required не отсекает, а сохранять блок с таким именем нельзя
              validate: (value) => (typeof value === 'string' && value.trim().length > 0) || t('common:required'),
            }}
          />

          {isLeafBlock ? (
            <BlockPointsField
              maxPoints={block.maxPoints}
              possiblePoints={block.possiblePoints}
              possibleValues={block.possibleValues}
              isProcessing={isUpdating}
            />
          ) : (
            <PointsRuleInput
              control={control}
              name="pointsRule"
              issues={block.pointsRule?.issues}
              disabled={!allowAdmin}
              isProcessing={isUpdating}
            />
          )}

          <Input
            control={control}
            name="unit"
            label={t('expertReport:unit')}
            placeholder={t('expertReport:unitPlaceholder')}
            disabled={!allowAdmin}
          />

          {allowAdmin && (
            <SaveButton
              className="justify-end"
              isDirty={formState.isDirty}
              allowSaveWithoutChanges={hasPointsFailure}
              isLoading={isUpdating}
              onClick={handleSubmit(onSubmit)}
              secondaryAction={
                <ConfirmationButton
                  onConfirm={handleDelete}
                  isLoadingConfirm={isUpdating}
                  title={t('expertReport:removeBlock')}
                  description={
                    <Trans
                      i18nKey="expertReport:removeBlockDescription"
                      values={{ blockName: block.name }}
                      components={{ strong: <strong /> }}
                    />
                  }
                  trigger={{
                    variant: 'outline',
                    color: 'destructive',
                    children: t('expertReport:deleteBlock'),
                  }}
                />
              }
            />
          )}
        </CardHeader>

        {/* Область прокрутки растянута на всю оставшуюся высоту карточки: иначе список правил
            уводит низ карточки за пределы экрана и до него нельзя долистать */}
        <CardContent className="flex-1 space-y-4 min-h-0 overflow-hidden">
          <ScrollArea className={'h-full'}>
            <RuleList isLeafBlock={isLeafBlock} isUpdating={isUpdating} />
          </ScrollArea>
        </CardContent>
      </Card>
    </FormProvider>
  );
};
